/**
 * The insight card library: which cards exist, and what each one can draw.
 *
 * A card declares the SHAPE it reads. That single declaration is what makes
 * both pickers safe in either direction — pick a card and only insights of
 * its shape are offered; pick an insight and only cards that read that shape
 * are offered. A card can never be handed data it cannot draw, and no screen
 * has to remember the rule.
 *
 * The ids and the shapes live in `@alluzo/shared`, because a template can now
 * pin a card and `validate-template.ts` must be able to reject an id no client
 * can draw. What stays here is how a card LOOKS: its widths, its name, whether
 * it plots. That split is what stops the two lists drifting, and a drift would
 * only ever show up as a card silently missing on somebody's phone.
 *
 * Two rules are encoded here rather than written down elsewhere:
 *
 *   spans   a card with a plot takes the whole row. On a 390pt screen only a
 *           plain number reads at half width.
 *   tone    a signed number wears the direction's colour, but the direction is
 *           carried by the sign, a word and an arrow first. Our green and red
 *           separate by 4.4 Delta E under deuteranopia, so colour may only
 *           ever repeat what the text already says, never replace it.
 */
import {
  INSIGHT_CARD_IDS,
  INSIGHT_CARD_SHAPE,
  type AppKey,
  type InsightCardId,
  type InsightShape,
} from '@alluzo/shared';

export {
  cardsForShape,
  INSIGHT_CARD_IDS,
  INSIGHT_CARD_SHAPE,
  isInsightCardId,
  type InsightCardId,
} from '@alluzo/shared';

export type InsightSpan = 'half' | 'full';

/**
 * The record's grid sizes, added BESIDE `InsightSpan` and never in place of it.
 *
 * A span is a width in the strip the Dashboard still draws — half a row or a
 * whole one. A size is a rectangle on the record's grid: Small is one cell,
 * Wide is two across, Large is two across and two down. The two coexist on
 * purpose (`REDESIGN.md` §12): a build already in the App Store reads `span`
 * and ignores `size`, so a downgrade loses the sizes and nothing else.
 */
export type InsightSize = 'small' | 'wide' | 'large';

export type InsightCard = {
  /** The one shape this card reads. Comes from shared, never restated. */
  readonly shape: InsightShape;
  /** Every width it may sit at. The first is what a new card gets. */
  readonly spans: readonly [InsightSpan, ...InsightSpan[]];
  /**
   * Every size it may sit at on the record's grid. The first is the default —
   * except a leader, which `defaultLayoutFor` widens, because "Top item" is a
   * name and a name does not fit a cell (`insight-layout.ts`).
   */
  readonly sizes: readonly [InsightSize, ...InsightSize[]];
  /**
   * The i18n key for its name in the library and the manage list.
   *
   * `AppKey`, not `string`: every one is written as a literal in `LOOKS`
   * below, so the compiler can check it against the catalogue and the five
   * screens that draw it keep the typed `t`.
   */
  readonly nameKey: AppKey;
  /** True when the card draws a plot, so it cannot shrink to half a row. */
  readonly plots: boolean;
};

type CardLook = Omit<InsightCard, 'shape'>;

const LOOKS: Readonly<Record<InsightCardId, CardLook>> = {
  /**
   * One number, labelled. Also serves a leader — "Top item" — because a
   * scalar result may carry the winning group's key, and a name over its
   * total is still one number with a label.
   */
  'insight.stat': {
    spans: ['half', 'full'],
    sizes: ['small', 'wide'],
    nameKey: 'insight.card.stat',
    plots: false,
  },
  /** The same number against the period before it. */
  'insight.delta': {
    spans: ['half', 'full'],
    sizes: ['small', 'wide'],
    nameKey: 'insight.card.delta',
    plots: false,
  },
  /** The same pair drawn as a bar against its reference, so the gap is visible. */
  'insight.progress': {
    spans: ['full'],
    sizes: ['wide'],
    nameKey: 'insight.card.progress',
    plots: true,
  },
  /** One series, one hue, as a line. */
  'insight.trend': {
    spans: ['full'],
    sizes: ['wide', 'large'],
    nameKey: 'insight.card.trend',
    plots: true,
  },
  /** The same series as periods you can name and compare. */
  'insight.bars': {
    spans: ['full'],
    sizes: ['wide', 'large'],
    nameKey: 'insight.card.bars',
    plots: true,
  },
  /** One measure, grouped and ranked, with the tail netted into Other. */
  'insight.breakdown': {
    spans: ['full'],
    sizes: ['large', 'wide'],
    nameKey: 'insight.card.breakdown',
    plots: true,
  },
};

/** The library: how each card looks, with the shape it reads from shared. */
export const INSIGHT_CARDS: Readonly<Record<InsightCardId, InsightCard>> = Object.fromEntries(
  INSIGHT_CARD_IDS.map((id) => [id, { ...LOOKS[id], shape: INSIGHT_CARD_SHAPE[id] }]),
) as Readonly<Record<InsightCardId, InsightCard>>;

/** The width a card takes when it is first placed. */
export const defaultSpanFor = (card: InsightCardId): InsightSpan => INSIGHT_CARDS[card].spans[0];

/** Whether this card may sit at this width. The manage screen disables the rest. */
export const allowsSpan = (card: InsightCardId, span: InsightSpan): boolean =>
  INSIGHT_CARDS[card].spans.includes(span);

/** The size a card takes when it is first placed on the record's grid. */
export const defaultSizeFor = (card: InsightCardId): InsightSize => INSIGHT_CARDS[card].sizes[0];

/** Whether this card may sit at this size. The resize handle offers no other. */
export const allowsSize = (card: InsightCardId, size: InsightSize): boolean =>
  INSIGHT_CARDS[card].sizes.includes(size);
